import { useEffect, useRef, useState } from 'react';

import { Copy01Icon, Refresh03Icon, ShieldKeyIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useClipboard } from 'hooks/clipboard';
import { useToast } from 'hooks/toast';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { Button, FormMessage } from 'components/FormComponents';

const copyToClipboard = useClipboard();
const showToast = useToast();

const handleCopyToClipboard = (otp, message) => {
  if (!otp) return;
  copyToClipboard(otp);
  showToast('Copied', message, 'info');
};

const formatTime = seconds => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

export const PortalOtpModal = ({ visible, candidateEmail, onGetOtp, onGenerate, onClose }) => {
  const [otp, setOtp] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [existingExpiresAt, setExistingExpiresAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [expiryTotalSeconds, setExpiryTotalSeconds] = useState(0);
  const intervalRef = useRef(null);

  const clearCountdown = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const startCountdown = expiresAtStr => {
    clearCountdown();
    const remainingSeconds = () =>
      Math.max(0, Math.floor((new Date(expiresAtStr) - Date.now()) / 1000));
    const tick = () => {
      const diff = remainingSeconds();
      setSecondsLeft(diff);
      if (diff <= 0) clearCountdown();
    };
    setExpiryTotalSeconds(remainingSeconds());
    tick();
    intervalRef.current = setInterval(tick, 1000);
  };

  useEffect(() => {
    if (visible) {
      setFetching(true);
      onGetOtp()
        .then(result => {
          if (result?.active && result?.expires_at) {
            setExistingExpiresAt(result.expires_at);
            startCountdown(result.expires_at);
          }
        })
        .catch(() => {})
        .finally(() => setFetching(false));
    } else {
      clearCountdown();
      setOtp(null);
      setExistingExpiresAt(null);
      setSecondsLeft(0);
      setExpiryTotalSeconds(0);
    }
  }, [visible]);

  useEffect(() => () => clearCountdown(), []);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const result = await onGenerate();
      if (result?.otp) {
        setOtp(result.otp);
        setExistingExpiresAt(null);
        startCountdown(result.expires_at);
      }
    } finally {
      setLoading(false);
    }
  };

  const isExpired = secondsLeft <= 0 && otp !== null;
  const progressPercent =
    expiryTotalSeconds > 0
      ? Math.min(100, Math.max(0, (secondsLeft / expiryTotalSeconds) * 100))
      : 0;
  const timerColor = secondsLeft < 60 ? '#e53e3e' : secondsLeft < 180 ? '#d97706' : '#38a169';

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      small
      title="Portal OTP"
      icon={ShieldKeyIcon}
      oldIconLibrary={false}>
      <div className="px-16 py-4 flex flex-col items-center gap-4 flex flex-col gap-8">
        <p className="text-2xl font-regular text-neutral-6 text-center">
          Read this code to the candidate, or copy it to share - use it when email delivery fails.
        </p>

        {fetching ? (
          <p className="text-sm text-gray-400">Checking for active OTP...</p>
        ) : otp ? (
          <>
            <div className="flex gap-2">
              {otp.split('').map((digit, i) => (
                <span
                  key={i}
                  className="flex items-center justify-center w-20 h-24 rounded-lg bg-neutral-2 border border-neutral-4 text-4xl font-bold text-neutral-7 select-all shadow-sm">
                  {digit}
                </span>
              ))}
            </div>

            <div className="w-full flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-1000"
                  style={{ width: progressPercent + '%', backgroundColor: timerColor }}
                />
              </div>
              <span
                className="text-lg font-semibold min-w-[38px] text-right"
                style={{ color: isExpired ? '#e53e3e' : timerColor }}>
                {isExpired ? 'Expired' : formatTime(secondsLeft)}
              </span>
            </div>

            <FormMessage
              message="Visible once - copy or note it before closing this window."
              type="Warning"
              noMargin
              noBorder
              closeable={false}
              className="justify-center"
            />

            <div className="flex gap-2 w-full">
              <Button
                type="primary"
                className="w-full"
                loading={loading}
                onClick={() => handleCopyToClipboard(otp, 'Code copied to clipboard')}>
                <HugeiconsIcon icon={Copy01Icon} size="20" className="mr-2" /> Copy code
              </Button>
              <Button type="secondary" className="w-1/3" loading={loading} onClick={handleGenerate}>
                <HugeiconsIcon icon={Refresh03Icon} size="20" className="mr-2" /> New
              </Button>
            </div>
          </>
        ) : (
          <>
            {existingExpiresAt && secondsLeft > 0 && (
              <div className="w-full rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-xl text-amber-800">
                A code was already sent to this candidate and expires in{' '}
                <span className="font-semibold">{formatTime(secondsLeft)}</span>. Generating a new
                one will invalidate it.
              </div>
            )}
            <Button type="primary" className="w-full" loading={loading} onClick={handleGenerate}>
              <HugeiconsIcon icon={Refresh03Icon} size="20" className="mr-2" />{' '}
              {existingExpiresAt && secondsLeft > 0 ? 'Generate New OTP' : 'Generate OTP'}
            </Button>
          </>
        )}

        {candidateEmail && (
          <p className="text-2xl text-text-tertiary">
            for{' '}
            <span className="border border-neutral-4 rounded-full px-4 py-2 bg-neutral-2 text-neutral-7 font-medium">
              {candidateEmail}
            </span>
          </p>
        )}
      </div>
    </AnimatedModal>
  );
};
